import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Star, Calendar, Gauge } from 'lucide-react';
import axios from 'axios';
import { resolveImageUrl } from '../utils/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FeaturedCarousel = () => {
  const [featuredCars, setFeaturedCars] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedCars();
  }, []);

  const fetchFeaturedCars = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cars/featured/list`);
      setFeaturedCars(response.data);
    } catch (error) {
      console.error('Error fetching featured cars:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % featuredCars.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + featuredCars.length) % featuredCars.length);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('pt-PT').format(mileage);
  };

  // Auto-advance carousel
  useEffect(() => {
    if (featuredCars.length <= 1) return;
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [featuredCars.length]);

  if (loading) {
    return null;
  }

  if (featuredCars.length === 0) {
    return null;
  }

  const defaultImage = 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800';

  return (
    <section className="py-16 px-6 md:px-12 bg-gradient-to-b from-[#090A0F] to-[#12141D]" data-testid="featured-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Star className="text-red-500" size={24} fill="#DC2626" />
          <div>
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-red-500">
              Oportunidades
            </p>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">
              Viaturas em Destaque
            </h2>
          </div>
        </div>

        {/* Carousel */}
        <div className="relative overflow-hidden">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {featuredCars.map((car) => (
                <div key={car.id} className="w-full flex-shrink-0">
                  <Link 
                    to={`/viatura/${car.id}`}
                    className="block group"
                    data-testid={`featured-car-${car.id}`}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8 bg-[#12141D] border border-red-500/30 p-4 sm:p-6 lg:p-8 mx-2 sm:mx-0">
                      {/* Image */}
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img 
                          src={resolveImageUrl(car.images?.[0]) || defaultImage}
                          alt={`${car.brand} ${car.model}`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 text-sm font-semibold">
                          <Star size={14} fill="white" />
                          OPORTUNIDADE
                        </div>
                      </div>

                      {/* Details */}
                      <div className="flex flex-col justify-center">
                        <p className="uppercase tracking-[0.15em] text-xs font-semibold text-zinc-500 mb-2">
                          {car.brand}
                        </p>
                        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-4 group-hover:text-red-500 transition-colors">
                          {car.model}
                        </h3>
                        <p className="text-4xl font-bold text-red-500 mb-6">
                          {formatPrice(car.price)}
                        </p>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                          <div className="flex items-center gap-2 text-zinc-400">
                            <Calendar size={18} className="text-red-500" />
                            <span>{car.year}</span>
                          </div>
                          <div className="flex items-center gap-2 text-zinc-400">
                            <Gauge size={18} className="text-red-500" />
                            <span>{formatMileage(car.mileage)} km</span>
                          </div>
                        </div>

                        <div className="inline-flex items-center gap-2 text-red-500 font-semibold group-hover:gap-3 transition-all">
                          Ver detalhes
                          <ChevronRight size={20} />
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          {featuredCars.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/60 hover:bg-red-600 flex items-center justify-center transition-colors z-10"
                data-testid="featured-prev-btn"
              >
                <ChevronLeft size={24} className="text-white" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/60 hover:bg-red-600 flex items-center justify-center transition-colors z-10"
                data-testid="featured-next-btn"
              >
                <ChevronRight size={24} className="text-white" />
              </button>
            </>
          )}

          {/* Dots */}
          {featuredCars.length > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              {featuredCars.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex ? 'bg-red-500 w-6' : 'bg-zinc-600 hover:bg-zinc-500'
                  }`}
                  data-testid={`featured-dot-${index}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedCarousel;
